import {
  CompleteTaskFailureParams,
  CompleteTaskResponse,
  CompleteTaskSuccessParams,
  LogInStates,
  SendOnTheWayStatus,
  Task,
} from './types';
import {NativeEventEmitter, NativeModules, Platform} from 'react-native';

import {Analytics} from '../analytics/analytics';
import {StorageKeys} from '../storage/type';
import {storage} from '../storage';

export const OnfleetSDK = NativeModules.OnfleetSDK;

export const OnfleetEventHandler = new NativeEventEmitter(OnfleetSDK);

class OnfleetService {
  private phoneNumber: string;

  private password: string;

  private silentLoginDone = false;

  constructor() {
    this.phoneNumber = '';
    this.password = '';
  }

  async fetchDriver(): Promise<any> {
    return await new Promise((resolve, _) => {
      OnfleetSDK?.fetchDriver((driver: any) => resolve(driver));
    });
  }

  async switchAccount(organizationName: string): Promise<string> {
    try {
      await OnfleetSDK?.switchAccount(organizationName);
      return '';
    } catch (e: any) {
      Analytics.eventWithProps('error', {
        type: 'onfleet',
        method: 'switchAccount',
        message: e.message,
        name: e.name,
      });
      return e.message;
    }
  }

  async silentLogin(prefix?: string, phone?: string, pass?: string) {
    if (this.silentLoginDone) return;
    this.silentLoginDone = true;
    const phoneNumber =
      phone ?? (await storage.getItem(StorageKeys.onfleetPhoneNumber));
    const onfleetPhoneNumberPrefix =
      prefix ?? (await storage.getItem(StorageKeys.onfleetPhoneNumberPrefix));
    const password =
      pass ?? (await storage.getItem(StorageKeys.onfleetPassword));
    if (phoneNumber && password && onfleetPhoneNumberPrefix)
      try {
        await new Promise((resolve, reject) => {
          OnfleetSDK?.authenticate(
            onfleetPhoneNumberPrefix + phoneNumber,
            password,
            (code: LogInStates, message: string) => {
              switch (code) {
                case LogInStates.loggedIn:
                  resolve(message);
                  break;
                case LogInStates.provisioning:
                case LogInStates.authenticating:
                  break;
                case LogInStates.logInFailed:
                  reject(new Error(message));
                  break;
                default:
                  reject(new Error('Something went wrong'));
                  break;
              }
            },
          );
        });
      } catch (err: any) {
        Analytics.eventWithProps('error', {
          type: 'onfleet',
          method: 'silentLogin',
          message: err.message,
          name: err.name,
        });
        storage.delete(StorageKeys.onfleetPhoneNumber);
        storage.delete(StorageKeys.onfleetPassword);
      }
    return false;
  }

  async login(
    phoneNumber: string,
    password: string,
    callback: (loginState: LogInStates, message: string) => void,
  ) {
    try {
      await new Promise((resolve, reject) => {
        OnfleetSDK?.authenticate(
          phoneNumber,
          password,
          (code: LogInStates, message: string) => {
            switch (code) {
              case LogInStates.loggedIn:
                callback(LogInStates.loggedIn, 'Successfully logged in');
                resolve(message);
                break;
              case LogInStates.provisioning:
                callback(
                  LogInStates.provisioning,
                  `Verifying Device... This operation can take up to ${message} seconds.`,
                );
                break;
              case LogInStates.authenticating:
                callback(LogInStates.authenticating, message);
                break;
              case LogInStates.logInFailed:
                callback(LogInStates.logInFailed, message);
                reject(new Error(message));
                break;
              default:
                callback(
                  LogInStates.logInFailed,
                  message ?? 'Something went wrong',
                );
                reject(new Error(message ?? 'Something went wrong'));
                break;
            }
          },
        );
      });
      storage.setItem(StorageKeys.onfleetPhoneNumber, phoneNumber.slice(-10));
      const prefix = phoneNumber.slice(0, -10);
      if (prefix === '+91' || prefix === '+1')
        storage.setItem(StorageKeys.onfleetPhoneNumberPrefix, prefix);
      storage.setItem(StorageKeys.onfleetPassword, password);
      this.phoneNumber = phoneNumber;
      this.password = password;
      await new Promise(r => setTimeout(r, 1000));
      return true;
    } catch (err: any) {
      Analytics.eventWithProps('error', {
        type: 'onfleet',
        method: 'login',
        message: err.message,
        name: err.name,
      });
      return false;
    }
  }

  logout() {
    OnfleetSDK?.logoutDriver();
    storage.delete(StorageKeys.onfleetPhoneNumber);
    storage.delete(StorageKeys.onfleetPassword);
    this.phoneNumber = '';
    this.password = '';
  }

  startListeners() {
    if (Platform.OS === 'ios') OnfleetSDK?.startListeners();
  }

  async fetchTasks(): Promise<Task[]> {
    try {
      return await OnfleetSDK?.fetchTasks();
    } catch (err: any) {
      Analytics.eventWithProps('error', {
        type: 'onfleet',
        method: 'fetchTasks',
        message: err.message,
        name: err.name,
      });
      return [];
    }
  }

  async sendOnTheWay(taskId: string): Promise<boolean> {
    try {
      const res = await OnfleetSDK?.sendOnTheWay(taskId);
      await new Promise(r => setTimeout(r, 2000));
      if (Platform.OS === 'android' && res !== SendOnTheWayStatus.SUCCESS)
        throw new Error(res);
      await new Promise(r => setTimeout(r, 1000));
      return res;
    } catch (err: any) {
      Analytics.eventWithProps('error', {
        type: 'onfleet',
        method: 'sendOnTheWay',
        message: err.message,
        name: err.name,
      });
      return false;
    }
  }

  startLocationTracking() {
    OnfleetSDK?.startLocationTracking();
  }

  stopLocationTracking() {
    OnfleetSDK?.stopLocationTracking();
  }

  async completeTask(
    taskId: string,
    details: CompleteTaskSuccessParams | CompleteTaskFailureParams,
  ): Promise<boolean> {
    try {
      const res = await OnfleetSDK?.completeTask(taskId, details);
      if (Platform.OS === 'android' && res !== CompleteTaskResponse.SUCCESS)
        throw new Error(res);
      await new Promise(r => setTimeout(r, 1000));
      return res;
    } catch (err: any) {
      Analytics.eventWithProps('error', {
        type: 'onfleet',
        method: 'completeTask',
        message: err.message,
        name: err.name,
      });
      return false;
    }
  }

  uploadSignature(keyId: string, path: string, imageName: string) {
    try {
      OnfleetSDK?.uploadSignature(keyId, path, imageName);
    } catch (err: any) {
      Analytics.eventWithProps('error', {
        type: 'onfleet',
        method: 'uploadSignature',
        message: err.message,
        name: err.name,
      });
    }
  }

  uploadImage(keyId: string, path: string, imageName: string) {
    try {
      OnfleetSDK?.uploadImage(keyId, path, imageName);
    } catch (err: any) {
      Analytics.eventWithProps('error', {
        type: 'onfleet',
        method: 'uploadImage',
        message: err.message,
        name: err.name,
      });
    }
  }

  async fetchActiveTask(): Promise<Task | undefined> {
    try {
      const res = await OnfleetSDK?.fetchActiveTask();
      return res as Task;
    } catch (err: any) {
      return undefined;
    }
  }
}

export default OnfleetService;
